import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * /todays-pull-ig
 * Temple's daily Instagram image generator for fluid card pulls.
 *
 * Flow:
 *   1. Upload phone photo of today's card pull
 *   2. Adjust position/zoom inside 1080x1080 frame
 *   3. App adds branded header (date + eyebrow) + footer (brand + IG handle)
 *   4. One click → download 1080x1080 PNG ready for Instagram
 *
 * No format prescription on the photo itself — the card can be tarot, oracle,
 * archangel, animal spirit, the MBSH deck, single card, multiple cards.
 * Divine picks the deck. Temple picks the photo. This tool wraps it in brand.
 */

const BRAND = {
  cream:   "#efeae7",
  teal:    "#068fa2",
  deep:    "#042F2E",
  gold:    "#d4af6a",
  bright:  "#25a1c2",
};

const SIZE = 1080; // Instagram square

const monthLong = (d) =>
  d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const TodaysPullIG = () => {
  const [photoSrc, setPhotoSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [bgChoice, setBgChoice] = useState("cream"); // cream | teal | deep
  const [customEyebrow, setCustomEyebrow] = useState("");
  const [showFrame, setShowFrame] = useState(true);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // SEO (this page is internal, so light meta only)
  useEffect(() => {
    document.title = "Today's Pull · Instagram Generator · Temple Franklin";
  }, []);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoSrc(ev.target.result);
      setZoom(1);
      setOffsetX(0);
      setOffsetY(0);
    };
    reader.readAsDataURL(file);
  };

  // Render to canvas (off-screen, 1080×1080) and trigger download
  const downloadPNG = () => {
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d");

    // Background fill
    const bgColor = bgChoice === "cream" ? BRAND.cream : bgChoice === "teal" ? BRAND.teal : BRAND.deep;
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, SIZE, SIZE);

    const isDarkBg = bgChoice !== "cream";
    const textPrimary = isDarkBg ? BRAND.cream : BRAND.deep;
    const accent = isDarkBg ? BRAND.gold : BRAND.teal;

    // Photo (centered, fit cover with user zoom/offset)
    if (photoSrc && imgRef.current && imgRef.current.complete) {
      const img = imgRef.current;
      const photoArea = { x: 90, y: 200, w: SIZE - 180, h: SIZE - 360 };

      // calc base "cover" scale
      const scaleBase = Math.max(photoArea.w / img.naturalWidth, photoArea.h / img.naturalHeight);
      const scale = scaleBase * zoom;
      const drawW = img.naturalWidth * scale;
      const drawH = img.naturalHeight * scale;
      const cx = photoArea.x + photoArea.w / 2 + offsetX;
      const cy = photoArea.y + photoArea.h / 2 + offsetY;
      const dx = cx - drawW / 2;
      const dy = cy - drawH / 2;

      // clip to photo area with optional frame
      ctx.save();
      ctx.beginPath();
      ctx.rect(photoArea.x, photoArea.y, photoArea.w, photoArea.h);
      ctx.clip();
      ctx.drawImage(img, dx, dy, drawW, drawH);
      ctx.restore();

      if (showFrame) {
        ctx.strokeStyle = accent;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.5;
        ctx.strokeRect(photoArea.x, photoArea.y, photoArea.w, photoArea.h);
        ctx.globalAlpha = 1;
      }
    }

    // ── HEADER ─────────────────────────────────────────────────────
    // Eyebrow (custom override or default)
    const eyebrow = (customEyebrow || "TODAY'S PULL").toUpperCase();
    ctx.fillStyle = accent;
    ctx.font = "600 22px 'Helvetica Neue', Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    // letter-spaced (manual)
    const letterSpace = (text, x, y, size, color, weight = "600") => {
      ctx.fillStyle = color;
      ctx.font = `${weight} ${size}px 'Helvetica Neue', Arial, sans-serif`;
      const chars = text.split("");
      const spacing = size * 0.28;
      const widths = chars.map((c) => ctx.measureText(c).width);
      const totalW = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
      let cur = x - totalW / 2;
      chars.forEach((c, i) => {
        ctx.fillText(c, cur + widths[i] / 2, y);
        cur += widths[i] + spacing;
      });
    };
    letterSpace(eyebrow, SIZE / 2, 90, 22, accent, "600");

    // Date
    ctx.fillStyle = textPrimary;
    ctx.font = "italic 28px Georgia, 'Cormorant Garamond', serif";
    ctx.fillText(monthLong(new Date()), SIZE / 2, 138);

    // Tiny divider
    ctx.fillStyle = accent;
    ctx.fillRect(SIZE / 2 - 30, 165, 60, 1.5);
    ctx.beginPath();
    ctx.arc(SIZE / 2, 165, 3, 0, Math.PI * 2);
    ctx.fill();

    // ── FOOTER ─────────────────────────────────────────────────────
    // Tiny divider
    ctx.fillRect(SIZE / 2 - 30, SIZE - 165, 60, 1.5);
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE - 165, 3, 0, Math.PI * 2);
    ctx.fill();

    // Brand wordmark
    letterSpace("MIND BODY SPIRIT HYGIENE\u2122 TOOLS", SIZE / 2, SIZE - 115, 16, textPrimary, "600");

    // Tagline
    ctx.fillStyle = textPrimary;
    ctx.globalAlpha = 0.72;
    ctx.font = "italic 18px Georgia, serif";
    ctx.fillText("real tools. real women. in the messy middle.", SIZE / 2, SIZE - 82);

    // IG handle / website
    ctx.globalAlpha = 1;
    ctx.fillStyle = accent;
    ctx.font = "500 17px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillText("@MindBodySpiritHygiene  \u00b7  mindbodyspirithygienetools.com/draw", SIZE / 2, SIZE - 50);

    // Download
    const dateStr = new Date().toISOString().split("T")[0];
    const link = document.createElement("a");
    link.download = `todays-pull-${dateStr}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const previewBg = bgChoice === "cream" ? BRAND.cream : bgChoice === "teal" ? BRAND.teal : BRAND.deep;
  const previewTextDark = bgChoice === "cream";
  const previewAccent = previewTextDark ? BRAND.teal : BRAND.gold;
  const previewText = previewTextDark ? BRAND.deep : BRAND.cream;

  return (
    <div className="min-h-screen bg-[#efeae7] py-16 px-4" data-testid="todays-pull-ig-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto text-center mb-10">
        <Link to="/draw" className="text-[#068fa2] underline text-sm" data-testid="back-to-draw">
          ← back to /draw
        </Link>
        <h1 className="font-serif italic text-4xl md:text-5xl text-[#042F2E] mt-4 mb-3">
          Today's Pull · Instagram Generator
        </h1>
        <p className="text-[#042F2E]/70 text-sm max-w-xl mx-auto leading-relaxed">
          Upload today's card photo. The tool adds your branded header + footer. Export a 1080×1080 PNG ready for Instagram.
          The card can be anything Divine moved you to pull.
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid md:grid-cols-[420px_1fr] gap-10">
        {/* CONTROLS */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#042F2E]/10 space-y-5" data-testid="controls">
          <div>
            <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
              1 · Upload today's card photo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              data-testid="photo-upload"
              className="block w-full text-sm text-[#042F2E] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:bg-[#042F2E] file:text-[#efeae7] hover:file:bg-[#068fa2] file:cursor-pointer file:tracking-wider"
            />
            <p className="text-[11px] text-[#042F2E]/50 mt-2">
              Any photo works. Top-down on your table is best.
            </p>
          </div>

          {photoSrc && (
            <>
              <div>
                <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
                  2 · Zoom (fit / fill the frame)
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.5"
                  step="0.05"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  data-testid="zoom"
                  className="w-full"
                />
                <div className="text-[11px] text-[#042F2E]/50 mt-1">{Math.round(zoom * 100)}%</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
                    Shift X
                  </label>
                  <input
                    type="range"
                    min="-300"
                    max="300"
                    step="5"
                    value={offsetX}
                    onChange={(e) => setOffsetX(parseFloat(e.target.value))}
                    data-testid="offset-x"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
                    Shift Y
                  </label>
                  <input
                    type="range"
                    min="-300"
                    max="300"
                    step="5"
                    value={offsetY}
                    onChange={(e) => setOffsetY(parseFloat(e.target.value))}
                    data-testid="offset-y"
                    className="w-full"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
              3 · Background
            </label>
            <div className="flex gap-2">
              {[
                { id: "cream", label: "Cream", color: BRAND.cream, border: true },
                { id: "teal",  label: "Teal",  color: BRAND.teal },
                { id: "deep",  label: "Deep",  color: BRAND.deep },
              ].map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBgChoice(b.id)}
                  data-testid={`bg-${b.id}`}
                  className={`flex-1 py-2 rounded-full text-xs tracking-wider font-medium transition ${
                    bgChoice === b.id
                      ? "ring-2 ring-[#042F2E] ring-offset-2"
                      : "opacity-70 hover:opacity-100"
                  } ${b.border ? "border border-[#042F2E]/20" : ""}`}
                  style={{
                    backgroundColor: b.color,
                    color: b.id === "cream" ? BRAND.deep : BRAND.cream,
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-2">
              4 · Eyebrow text (optional)
            </label>
            <input
              type="text"
              value={customEyebrow}
              onChange={(e) => setCustomEyebrow(e.target.value)}
              placeholder="TODAY'S PULL (default)"
              data-testid="eyebrow"
              className="w-full text-sm border border-[#042F2E]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#068fa2]"
            />
            <p className="text-[11px] text-[#042F2E]/50 mt-2">
              Try: SUNDAY NOTE · WEEK RESET · TUESDAY MORNING · or leave blank
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="frame"
              checked={showFrame}
              onChange={(e) => setShowFrame(e.target.checked)}
              data-testid="frame-toggle"
            />
            <label htmlFor="frame" className="text-sm text-[#042F2E]">
              Show thin accent frame around photo
            </label>
          </div>

          <button
            onClick={downloadPNG}
            disabled={!photoSrc}
            data-testid="download-btn"
            className="w-full py-3 mt-2 rounded-full bg-[#042F2E] text-[#efeae7] text-sm font-medium tracking-wider hover:bg-[#068fa2] transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ⬇ Download 1080×1080 PNG
          </button>

          <div className="pt-4 border-t border-[#042F2E]/10">
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#068fa2] font-medium mb-2">
              Caption template
            </p>
            <p className="text-[12px] text-[#042F2E]/75 leading-relaxed">
              [the line that came through]<br /><br />
              [the message Divine moved through you — as long or as short as it lands]<br /><br />
              The Walk:<br />
              [the practice from the card]<br /><br />
              —<br /><br />
              Take what resonates. Leave the rest. You have your own free will and your own discernment.<br /><br />
              🪶 Pull your own → link in bio<br /><br />
              #dailycardpull #oraclecards #spiritualpractice #morningritual #mindbodyspirithygiene
            </p>
          </div>
        </div>

        {/* LIVE PREVIEW */}
        <div data-testid="preview-wrapper">
          <p className="text-xs tracking-[0.3em] uppercase text-[#068fa2] font-medium mb-3 text-center">
            Live preview · 1080×1080
          </p>
          <div className="relative mx-auto" style={{ width: "100%", maxWidth: 520, aspectRatio: "1 / 1" }}>
            <div
              ref={canvasRef}
              className="w-full h-full rounded-lg shadow-xl overflow-hidden relative"
              style={{ backgroundColor: previewBg }}
              data-testid="preview"
            >
              {/* HEADER */}
              <div className="absolute top-0 left-0 right-0 pt-[6.5%] text-center">
                <div
                  className="font-sans font-semibold"
                  style={{
                    fontSize: "1.2vmin",
                    letterSpacing: "0.35em",
                    color: previewAccent,
                  }}
                >
                  {(customEyebrow || "TODAY'S PULL").toUpperCase()}
                </div>
                <div
                  className="font-serif italic mt-1"
                  style={{
                    fontSize: "1.7vmin",
                    color: previewText,
                  }}
                >
                  {monthLong(new Date())}
                </div>
                <div
                  className="mt-2 mx-auto"
                  style={{ width: "5%", height: 1, backgroundColor: previewAccent }}
                />
              </div>

              {/* PHOTO */}
              {photoSrc ? (
                <div
                  className="absolute"
                  style={{
                    left: "8.3%",
                    right: "8.3%",
                    top: "18.5%",
                    bottom: "18.5%",
                    overflow: "hidden",
                    border: showFrame ? `1px solid ${previewAccent}80` : "none",
                  }}
                >
                  <img
                    ref={imgRef}
                    src={photoSrc}
                    alt="Today's pull"
                    crossOrigin="anonymous"
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: `translate(-50%, -50%) translate(${offsetX / 2.16}px, ${offsetY / 2.16}px) scale(${zoom})`,
                      minWidth: "100%",
                      minHeight: "100%",
                      objectFit: "cover",
                      transition: "transform 0.05s linear",
                    }}
                  />
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-center px-10">
                  <div style={{ color: previewText, opacity: 0.5 }}>
                    <p className="font-serif italic text-2xl mb-2">Upload your card photo</p>
                    <p className="text-xs tracking-widest uppercase">to see the preview</p>
                  </div>
                </div>
              )}

              {/* FOOTER */}
              <div className="absolute bottom-0 left-0 right-0 pb-[5%] text-center">
                <div
                  className="mb-3 mx-auto"
                  style={{ width: "5%", height: 1, backgroundColor: previewAccent }}
                />
                <div
                  className="font-sans font-semibold"
                  style={{
                    fontSize: "0.95vmin",
                    letterSpacing: "0.32em",
                    color: previewText,
                  }}
                >
                  MIND BODY SPIRIT HYGIENE&trade; TOOLS
                </div>
                <div
                  className="font-serif italic mt-1"
                  style={{
                    fontSize: "1.05vmin",
                    color: previewText,
                    opacity: 0.72,
                  }}
                >
                  real tools. real women. in the messy middle.
                </div>
                <div
                  className="font-sans mt-2"
                  style={{
                    fontSize: "1vmin",
                    color: previewAccent,
                    fontWeight: 500,
                  }}
                >
                  @MindBodySpiritHygiene · mindbodyspirithygienetools.com/draw
                </div>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-[#042F2E]/50 mt-3 text-center">
            Preview is rendered live. Download exports at full 1080×1080 print resolution.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TodaysPullIG;
